import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Module, Material, Attendance } from '../../types/database';
import { Clock, MapPin, CheckCircle, ExternalLink, FileText, Video, FileDown, X, Upload, FileCheck, Lock, Image, ArrowLeft, ClipboardCheck, Trash2, ShieldCheck } from 'lucide-react';
import { VideoPlayerModal } from '../modals/VideoPlayerModal';
import { HTMLViewerModal } from '../library/HTMLViewerModal';
import { PDFViewerModal } from '../library/PDFViewerModal';
import { ModuleHTMLViewer } from './ModuleHTMLViewer';
import { TaskModal } from './TaskModal';
import { ProtocoloBimModal } from '../ProtocoloBimModal';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigation } from '../../contexts/NavigationContext';
import { Spinner } from '../ui/Spinner';
import { toast } from '../ui/Toaster';
import { fmtDateLong, fmtTimeRange } from '../../lib/datetime';
import { getModuleTask, getTaskSubmission, isTaskOpen } from '../../lib/tasks';
import type { CourseTask, TaskSubmission } from '../../types/tasks';

interface ModuleFile {
  id: string;
  file_name: string;
  file_url: string;
  file_type: string;
  file_size: number;
}

interface ModuleWithMaterials extends Module {
  materials: Material[];
  module_files: ModuleFile[];
  docente_name?: string;
}

interface CourseTimelineProps {
  courseId: string;
  courseName: string;
}

export function CourseTimeline({ courseId, courseName }: CourseTimelineProps) {
  const { profile } = useAuth();
  const { goBack } = useNavigation();
  const [modules, setModules] = useState<ModuleWithMaterials[]>([]);
  const [selectedModule, setSelectedModule] = useState<ModuleWithMaterials | null>(null);
  const [attendance, setAttendance] = useState<Record<string, boolean>>({});
  const [numeroDiplomado, setNumeroDiplomado] = useState<string | null>(null);
  const [protocoloOpen, setProtocoloOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'files' | 'submissions'>('files');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadingModuleFile, setUploadingModuleFile] = useState(false);
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [gradingSubmissionId, setGradingSubmissionId] = useState<string | null>(null);
  const [gradeInputs, setGradeInputs] = useState<Record<string, string>>({});
  const [studentSubmission, setStudentSubmission] = useState<any>(null);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState<{ url: string; title: string; embedCode?: string | null } | null>(null);
  const [isHTMLViewerOpen, setIsHTMLViewerOpen] = useState(false);
  const [selectedHTML, setSelectedHTML] = useState<{ url: string; title: string } | null>(null);
  const [isPDFViewerOpen, setIsPDFViewerOpen] = useState(false);
  const [selectedPDF, setSelectedPDF] = useState<{ url: string; title: string } | null>(null);
  const [moduleTask, setModuleTask] = useState<CourseTask | null>(null);
  const [taskSubmission, setTaskSubmission] = useState<TaskSubmission | null>(null);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [uploadingTask, setUploadingTask] = useState(false);
  const [taskUploadMsg, setTaskUploadMsg] = useState(''); // '' | 'ok' | 'error:...'
  const [confirmDeleteTask, setConfirmDeleteTask] = useState(false);

  // Ref al contenedor scrollable + flag para no auto-scrollear más de una vez
  const containerRef = useRef<HTMLDivElement>(null);
  const didAutoScrollRef = useRef(false);

  useEffect(() => {
    loadModules();
    loadProtocol();
    if (profile?.role === 'alumno') {
      loadAttendance();
    }
  }, [courseId, profile?.role]);

  useEffect(() => {
    if (selectedModule) {
      if (profile?.role === 'alumno') {
        loadStudentSubmission();
      } else if (profile?.role === 'docente') {
        loadAllSubmissions();
      }
    }
  }, [selectedModule?.id, profile?.role, profile?.id]);

  // Cargar tarea del módulo (alumnos ven su submission; docentes/admins ven si hay tarea asignada)
  useEffect(() => {
    if (!selectedModule) {
      setModuleTask(null);
      setTaskSubmission(null);
      return;
    }
    let active = true;
    async function load() {
      const task = await getModuleTask(selectedModule!.id);
      if (!active) return;
      setModuleTask(task);
      if (task && profile?.id && profile?.role === 'alumno') {
        const sub = await getTaskSubmission(task.id, profile.id);
        if (active) setTaskSubmission(sub);
      } else {
        if (active) setTaskSubmission(null);
      }
    }
    load();
    return () => { active = false; };
  }, [selectedModule?.id, profile?.role, profile?.id]);

  useEffect(() => {
    if (!selectedModule || !profile) return;

    const channel = supabase
      .channel(`module_submissions:${selectedModule.id}:${profile.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'module_submissions',
          filter: `module_id=eq.${selectedModule.id}`,
        },
        (payload) => {
          console.log('Realtime event received:', payload);

          if (profile.role === 'docente') {
            loadAllSubmissions();
          } else if (profile.role === 'alumno' && payload.new && payload.new.alumno_id === profile.id) {
            console.log('Updating student submission, new grade:', payload.new.grade);
            setStudentSubmission(payload.new);
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedModule?.id, profile?.id, profile?.role]);

  // Auto-scroll al módulo más cercano a hoy. Si hay una clase futura, se va
  // a esa (la próxima); si no, a la última que pasó. Solo lo hacemos UNA vez
  // (con didAutoScrollRef) para no saltar al usuario cuando hace scroll
  // manual y los módulos se actualizan por realtime u otra cosa.
  useEffect(() => {
    if (didAutoScrollRef.current) return;
    if (loading || modules.length === 0) return;
    if (!containerRef.current) return;

    const modsWithDate = modules.filter(m => m.start_date);
    if (modsWithDate.length === 0) return;

    const now = Date.now();
    // Próxima clase futura, o la última que pasó si todas son pasadas.
    const target =
      modsWithDate.find(m => new Date(m.start_date!).getTime() >= now) ??
      modsWithDate[modsWithDate.length - 1];

    // Esperar a que el DOM termine de pintar la card y después scrollear.
    const t = setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const el = container.querySelector(`[data-module-id="${target.id}"]`) as HTMLElement | null;
      if (!el) return;

      const containerRect = container.getBoundingClientRect();
      const elRect        = el.getBoundingClientRect();
      // Centrar la card activa en el viewport del contenedor.
      const targetScroll = container.scrollTop + (elRect.top - containerRect.top) - (containerRect.height / 2) + (elRect.height / 2);

      container.scrollTo({ top: Math.max(0, targetScroll), behavior: 'smooth' });
      didAutoScrollRef.current = true;
    }, 150);

    return () => clearTimeout(t);
  }, [loading, modules]);

  const loadProtocol = async () => {
    const { data } = await supabase
      .from('course_protocols')
      .select('numero_diplomado')
      .eq('course_id', courseId)
      .maybeSingle();
    if (data?.numero_diplomado) setNumeroDiplomado(data.numero_diplomado);
  };

  const loadModules = async () => {
    try {
      const { data: modulesData, error } = await supabase
        .from('modules')
        .select(`
          *,
          profiles:docente_id(full_name),
          module_materials(
            materials(*)
          ),
          module_files(*)
        `)
        .eq('course_id', courseId)
        .order('order_index', { ascending: true });

      if (error) throw error;

      const formattedModules = modulesData.map((mod: any) => ({
        ...mod,
        docente_name: mod.profiles?.full_name,
        materials: mod.module_materials?.map((mm: any) => mm.materials).filter(Boolean) || [],
        module_files: mod.module_files || [],
      }));

      setModules(formattedModules);
      return formattedModules;
    } catch (error) {
      console.error('Error loading modules:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAttendance = async () => {
    try {
      const { data, error } = await supabase
        .from('attendance')
        .select('module_id, present')
        .eq('alumno_id', profile!.id);

      if (error) throw error;

      const attendanceMap: Record<string, boolean> = {};
      data?.forEach((att: Attendance) => {
        attendanceMap[att.module_id] = att.present;
      });
      setAttendance(attendanceMap);
    } catch (error) {
      console.error('Error loading attendance:', error);
    }
  };

  const canMarkAttendance = (module: ModuleWithMaterials): boolean => {
    if (!module.start_date) return false;

    const moduleDate = new Date(module.start_date);
    const today = new Date();

    const moduleDateOnly = new Date(moduleDate.getFullYear(), moduleDate.getMonth(), moduleDate.getDate());
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    return moduleDateOnly.getTime() === todayDateOnly.getTime();
  };

  const markAttendance = async (moduleId: string) => {
    try {
      const { error } = await supabase
        .from('attendance')
        .insert({
          module_id: moduleId,
          alumno_id: profile!.id,
          present: true,
          marked_by: profile!.id,
        });

      if (error) throw error;
      loadAttendance();
    } catch (error) {
      console.error('Error marking attendance:', error);
    }
  };

  const loadStudentSubmission = async () => {
    if (!selectedModule || !profile?.id) return;
    try {
      const { data, error } = await supabase
        .from('module_submissions')
        .select('*')
        .eq('module_id', selectedModule.id)
        .eq('alumno_id', profile.id)
        .maybeSingle();

      if (error) throw error;
      console.log('Student submission loaded:', data);
      setStudentSubmission(data);
    } catch (error) {
      console.error('Error loading submission:', error);
    }
  };

  const loadAllSubmissions = async () => {
    if (!selectedModule) return;
    try {
      const { data, error } = await supabase
        .from('module_submissions')
        .select(`
          *,
          profiles:alumno_id(full_name)
        `)
        .eq('module_id', selectedModule.id)
        .order('submitted_at', { ascending: false });

      if (error) throw error;
      setSubmissions(data || []);
    } catch (error) {
      console.error('Error loading submissions:', error);
    }
  };

  const updateGrade = async (submissionId: string, grade: number) => {
    try {
      const { data, error } = await supabase
        .from('module_submissions')
        .update({ grade })
        .eq('id', submissionId)
        .select();

      if (error) throw error;

      console.log('Grade updated successfully:', data);

      setSubmissions(prev => prev.map(sub =>
        sub.id === submissionId
          ? { ...sub, grade: grade }
          : sub
      ));

      setGradingSubmissionId(null);
      setGradeInputs(prev => {
        const updated = { ...prev };
        delete updated[submissionId];
        return updated;
      });

      toast.success('Nota actualizada');
    } catch (error: any) {
      console.error('Error updating grade:', error);
      toast.error(error.message || 'Error al actualizar la nota');
    }
  };

  const handleGradeSubmit = (submissionId: string) => {
    const normalizedInput = (gradeInputs[submissionId] || '').replace(',', '.');
    const grade = parseFloat(normalizedInput);
    if (isNaN(grade) || grade < 1.0 || grade > 7.0) {
      toast.error('La nota debe estar entre 1.0 y 7.0');
      return;
    }
    updateGrade(submissionId, grade);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !selectedModule) return;

    const file = e.target.files[0];
    const allowedExtensions = /\.(png|jpe?g|pdf|rvt|rfa|rte|dwg|docx?|xlsx?|pptx?|zip|rar)$/i;

    if (!allowedExtensions.test(file.name)) {
      toast.error('Formato no permitido. Usa PNG, JPEG, PDF, RVT, RFA, RTE, DWG, DOCX, XLSX, PPTX, ZIP o RAR.');
      return;
    }

    if (file.size > 104857600) {
      toast.error('Archivo demasiado grande. Máximo 100MB.');
      return;
    }

    setUploadingFile(true);
    try {
      const timestamp = Date.now();
      const fileExtension = file.name.split('.').pop() || 'bin';
      const baseName = file.name.substring(0, file.name.lastIndexOf('.')) || file.name;

      // Sanitize and truncate filename to prevent issues
      const sanitizedBaseName = baseName
        .replace(/[^a-zA-Z0-9._-\s]/g, '_')
        .substring(0, 100);

      const sanitizedFileName = `${sanitizedBaseName}.${fileExtension}`;
      const filePath = `${profile!.id}/${selectedModule.id}/${timestamp}_${sanitizedFileName}`;

      const { error: uploadError } = await supabase.storage
        .from('module-submissions')
        .upload(filePath, file, {
          upsert: false,
          contentType: file.type || 'application/octet-stream'
        });

      if (uploadError) {
        console.error('Upload error details:', uploadError);
        throw new Error(`Error subiendo archivo: ${uploadError.message}`);
      }

      const { data: publicUrlData } = supabase.storage
        .from('module-submissions')
        .getPublicUrl(filePath);

      const publicUrl = publicUrlData.publicUrl;

      const { error: dbError } = await supabase
        .from('module_submissions')
        .upsert({
          module_id: selectedModule.id,
          alumno_id: profile!.id,
          file_name: file.name,
          file_url: publicUrl,
          file_type: fileExtension,
          file_size: file.size,
          submitted_at: new Date().toISOString(),
        }, {
          onConflict: 'module_id,alumno_id'
        });

      if (dbError) {
        console.error('Database error details:', dbError);
        throw new Error(`Error guardando en base de datos: ${dbError.message}`);
      }

      toast.success('Archivo subido');
      loadStudentSubmission();
    } catch (error: any) {
      console.error('Error uploading file:', error);
      toast.error(error.message || 'Error al subir archivo');
    } finally {
      setUploadingFile(false);
    }
  };

  const handleModuleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0] || !selectedModule) return;

    const file = e.target.files[0];
    const allowedExtensions = /\.(png|jpe?g|pdf|rvt|rfa|rte|dwg|docx?|xlsx?|pptx?|zip|rar)$/i;

    if (!allowedExtensions.test(file.name)) {
      toast.error('Formato no permitido. Usa PNG, JPEG, PDF, RVT, RFA, RTE, DWG, DOCX, XLSX, PPTX, ZIP o RAR.');
      e.target.value = '';
      return;
    }
    if (file.size > 104857600) {
      toast.error('Archivo demasiado grande. Máximo 100MB.');
      e.target.value = '';
      return;
    }

    setUploadingModuleFile(true);
    try {
      const fileExt = file.name.split('.').pop() || 'bin';
      const fileName = `module-${selectedModule.id}-${Date.now()}.${fileExt}`;
      const filePath = `modules/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('module-files')
        .upload(filePath, file, { upsert: false });

      if (uploadError) throw new Error(`Error subiendo archivo: ${uploadError.message}`);

      const { data: urlData } = supabase.storage.from('module-files').getPublicUrl(filePath);

      const extLower = fileExt.toLowerCase();
      const fileType = extLower === 'pdf' ? 'pdf'
        : ['png', 'jpg', 'jpeg'].includes(extLower) ? 'image'
        : ['mp4', 'mov', 'avi', 'mkv', 'webm'].includes(extLower) ? 'video'
        : ['doc', 'docx', 'xlsx', 'xls', 'pptx', 'ppt'].includes(extLower) ? 'document'
        : 'other';

      const { error: dbError } = await supabase.from('module_files').insert([{
        module_id:   selectedModule.id,
        file_name:   file.name,
        file_url:    urlData.publicUrl,
        file_type:   fileType,
        file_size:   file.size,
        uploaded_by: profile!.id,
      }]);

      if (dbError) throw new Error(`Error guardando en base de datos: ${dbError.message}`);

      toast.success('Recurso subido al módulo');
      const updatedModules = await loadModules();
      if (updatedModules) {
        const updated = updatedModules.find((m: any) => m.id === selectedModule.id);
        if (updated) setSelectedModule(updated);
      }
    } catch (error: any) {
      console.error('Error uploading module file:', error);
      toast.error(error.message || 'Error al subir recurso');
    } finally {
      setUploadingModuleFile(false);
      e.target.value = '';
    }
  };

  const handleTaskFolderUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !selectedModule) return;

    type FileWithRel = File & { webkitRelativePath: string };
    const firstRel = (files[0] as FileWithRel).webkitRelativePath;
    const folderName = firstRel.split('/')[0];

    if (!files.some(f => (f as FileWithRel).webkitRelativePath.endsWith('/index.html'))) {
      setTaskUploadMsg('error:La carpeta debe contener un index.html');
      e.target.value = '';
      return;
    }

    // Listener que se inyecta automáticamente en el index.html si no está presente
    const REVIEW_LISTENER = `\n  window.addEventListener('message',function(e){if(!e.data||e.data.type!=='cubus-start-review')return;review=true;if(Array.isArray(e.data.clicks)){e.data.clicks.forEach(function(c,i){if(c&&i<clicks.length)clicks[i]=c;});}doneCard.classList.remove('show');cur=0;render();});\n`;

    setUploadingTask(true);
    setTaskUploadMsg('');
    try {
      for (const file of files) {
        const storagePath = (file as FileWithRel).webkitRelativePath;
        let uploadData: File | Blob = file;
        // Inyectar listener de revisión en index.html si no lo tiene
        if (storagePath.endsWith('/index.html')) {
          const html = await file.text();
          if (!html.includes('cubus-start-review')) {
            const patched = html.replace('</script>', REVIEW_LISTENER + '</script>');
            uploadData = new Blob([patched], { type: 'text/html' });
          }
        }
        const { error } = await supabase.storage.from('guides-html').upload(storagePath, uploadData, { upsert: true });
        if (error) throw error;
      }

      const htmlPath = `${folderName}/index.html`;
      const taskTitle = `Tarea · ${selectedModule.name}`;
      const { data: existing } = await supabase.from('course_tasks').select('id').eq('module_id', selectedModule.id).maybeSingle();
      if (existing) {
        await supabase.from('course_tasks').update({ title: taskTitle, html_path: htmlPath, is_active: true }).eq('id', existing.id);
      } else {
        await supabase.from('course_tasks').insert({ module_id: selectedModule.id, title: taskTitle, html_path: htmlPath, is_active: true });
      }

      const task = await getModuleTask(selectedModule.id);
      setModuleTask(task);
      setTaskUploadMsg('ok');
    } catch (err: unknown) {
      setTaskUploadMsg(`error:${err instanceof Error ? err.message : 'Error al subir'}`);
    } finally {
      setUploadingTask(false);
      e.target.value = '';
    }
  };

  const handleDeleteTask = async () => {
    if (!moduleTask) return;
    const { error } = await supabase.from('course_tasks').delete().eq('id', moduleTask.id);
    if (error) { toast.error('Error al borrar la tarea'); return; }
    setModuleTask(null);
    setConfirmDeleteTask(false);
    setTaskUploadMsg('');
    toast.success('Tarea eliminada');
  };

  const getModuleStatus = (module: ModuleWithMaterials): 'completed' | 'active' | 'pending' => {
    if (!module.start_date) return 'pending';

    const startDate = new Date(module.start_date);
    const today = new Date();

    const startDateOnly = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const todayDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (todayDateOnly > startDateOnly) return 'completed';
    if (todayDateOnly.getTime() === startDateOnly.getTime()) return 'active';
    return 'pending';
  };

  const getStatusColor = (status: 'completed' | 'active' | 'pending') => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'completed':
        return 'bg-blue-500';
      case 'pending':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status: 'completed' | 'active' | 'pending') => {
    switch (status) {
      case 'active':
        return 'Activo';
      case 'completed':
        return 'Completado';
      case 'pending':
        return 'Pendiente';
      default:
        return status;
    }
  };

  const getFileIcon = (fileType: string) => {
    switch (fileType) {
      case 'video':
        return Video;
      case 'pdf':
      case 'document':
      case 'guide':
        return FileText;
      case 'image':
        return Image;
      default:
        return FileDown;
    }
  };

  const handleDownload = async (fileUrl: string, fileName: string) => {
    try {
      const response = await fetch(fileUrl, {
        method: 'GET',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();

      // Ensure fileName has proper extension
      let downloadName = fileName;
      if (!downloadName.includes('.')) {
        // Extract extension from URL if fileName doesn't have one
        const urlPath = fileUrl.split('?')[0];
        const urlExt = urlPath.split('.').pop()?.toLowerCase();
        if (urlExt && ['png', 'jpg', 'jpeg', 'pdf', 'docx', 'xlsx', 'pptx', 'zip', 'rar', 'rvt', 'rfa', 'rte', 'dwg'].includes(urlExt)) {
          downloadName = `${fileName}.${urlExt}`;
        }
      }

      // Create a proper blob with the correct MIME type based on file extension
      const extension = downloadName.split('.').pop()?.toLowerCase();
      const mimeTypes: Record<string, string> = {
        'png': 'image/png',
        'jpg': 'image/jpeg',
        'jpeg': 'image/jpeg',
        'pdf': 'application/pdf',
        'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'doc': 'application/msword',
        'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'xls': 'application/vnd.ms-excel',
        'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
        'ppt': 'application/vnd.ms-powerpoint',
        'zip': 'application/zip',
        'rar': 'application/x-rar-compressed',
        'rvt': 'application/octet-stream',
        'rfa': 'application/octet-stream',
        'rte': 'application/octet-stream',
        'dwg': 'application/acad'
      };

      const mimeType = extension ? (mimeTypes[extension] || blob.type) : blob.type;
      const properBlob = new Blob([blob], { type: mimeType });

      const url = window.URL.createObjectURL(properBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      toast.error('Error al descargar el archivo. Intenta nuevamente.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64 p-4 sm:p-6 lg:p-8">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div ref={containerRef} className="space-y-6 p-4 sm:p-6 lg:p-8 overflow-y-auto">
      <nav aria-label="Migas" className="flex items-center gap-1 text-sm">
        <button
          type="button"
          onClick={goBack}
          className="flex items-center gap-1.5 text-muted hover:text-cubus-lime transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cubus-lime rounded px-1"
        >
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
          Mis cursos
        </button>
        <span className="text-faint mx-1">/</span>
        <span className="text-white/80 truncate">{courseName}</span>
      </nav>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-display text-3xl md:text-4xl font-bold text-white tracking-tight mb-1">{courseName}</h1>
          <p className="text-muted">Timeline del curso</p>
        </div>
        <button
          type="button"
          onClick={() => setProtocoloOpen(true)}
          className="flex items-center gap-2 px-4 py-2 bg-cubus-lime/10 hover:bg-cubus-lime hover:text-cubus-bg text-cubus-lime border border-cubus-lime/40 rounded-lg transition-all duration-200 font-medium"
        >
          <ShieldCheck className="w-4 h-4" />
          Protocolo BIM CUBUS
        </button>
      </div>

      <ProtocoloBimModal
        open={protocoloOpen}
        onClose={() => setProtocoloOpen(false)}
        courseId={courseId}
      />

      <div className="relative max-w-7xl mx-auto">
        <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-1 bg-cubus-lime/30" />

        <div className="space-y-12">
          {modules.map((module, index) => {
            const moduleStatus = getModuleStatus(module);
            const isActive = moduleStatus === 'active';
            const isPending = moduleStatus === 'pending';
            const isCompleted = moduleStatus === 'completed';
            // Docentes y admins ven todo desbloqueado siempre
            const isLockedForUser = isPending && profile?.role === 'alumno';
            const hasAttendance = attendance[module.id];
            const showAbsent = profile?.role === 'alumno' && isCompleted && !hasAttendance;
            const isLeft = index % 2 === 0;

            return (
              <div
                key={module.id}
                data-module-id={module.id}
                className={`relative flex items-center ${isLeft ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`absolute left-1/2 -translate-x-1/2 w-5 h-5 rounded-full ${getStatusColor(moduleStatus)} border-4 border-white/[0.08] z-10 shadow-lg`} />

                <div className={`w-full ${isLeft ? 'pr-[52%]' : 'pl-[52%]'}`}>
                  {module.start_date && (
                    <div className={`text-sm text-muted mb-2 ${isLeft ? 'text-right' : 'text-left'}`}>
                      {fmtDateLong(module.start_date)}
                    </div>
                  )}

                  <div
                    className={`relative glass-surface rounded-xl p-6 border-2 border-white/[0.06] hover:border-cubus-lime/40 transition-all duration-300 cursor-pointer group shadow-xl ${
                      isLockedForUser ? 'opacity-60' : 'opacity-100'
                    } hover:shadow-2xl hover:shadow-cubus-lime/20`}
                    onClick={() => setSelectedModule(module)}
                  >
                    {isLockedForUser ? (
                      <div className="absolute top-4 right-4 p-2 bg-gray-700 rounded-lg">
                        <Lock className="w-5 h-5 text-muted" />
                      </div>
                    ) : (
                      <div className="absolute top-4 right-4 p-2 bg-cubus-lime/10 rounded-lg">
                        <CheckCircle className="w-5 h-5 text-cubus-lime" />
                      </div>
                    )}
                    <div className="space-y-4">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                          isActive ? 'bg-green-500 text-white' :
                          isCompleted ? 'bg-blue-500 text-white' :
                          'bg-gray-600 text-white/70'
                        }`}>
                          {getStatusLabel(moduleStatus)}
                        </span>
                        {hasAttendance && (
                          <span className="flex items-center gap-1 px-3 py-1 rounded-full bg-cubus-lime text-cubus-bg text-xs font-bold">
                            <CheckCircle className="w-3 h-3" />
                            Presente
                          </span>
                        )}
                        {showAbsent && (
                          <span className="px-3 py-1 rounded-full bg-red-500 text-white text-xs font-bold">
                            Ausente
                          </span>
                        )}
                      </div>

                      <div>
                        <h3 className="text-2xl font-bold text-white group-hover:text-cubus-lime transition-colors mb-2">
                          {module.name}
                        </h3>
                        <p className="text-muted leading-relaxed">{module.description}</p>
                      </div>

                      <div className="flex flex-wrap gap-4 pt-2 border-t border-white/[0.08]">
                        {module.docente_name && (
                          <div className="flex items-center gap-2 text-white/70">
                            <MapPin className="w-4 h-4 text-cubus-lime" />
                            <span className="text-sm font-medium">{module.docente_name}</span>
                          </div>
                        )}
                        {(module.materials.length > 0 || module.module_files.length > 0) && (
                          <div className="flex items-center gap-2 text-cubus-lime">
                            <FileText className="w-4 h-4" />
                            <span className="text-sm font-medium">{module.materials.length + module.module_files.length} recursos</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedModule && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4" onClick={() => setSelectedModule(null)}>
          <div className="glass-surface rounded-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 glass-surface border-b border-white/[0.06] p-6 flex items-start justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white mb-2">{selectedModule.name}</h2>
                <p className="text-muted">{selectedModule.description}</p>
              </div>
              <button
                onClick={() => setSelectedModule(null)}
                className="p-2 hover:bg-white/[0.04] rounded-lg transition-colors"
              >
                <X className="w-6 h-6 text-muted" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="glass-deep rounded-lg p-4 border border-white/[0.08]">
                  <p className="text-sm text-muted mb-1">Docente</p>
                  <p className="text-white font-medium">{selectedModule.docente_name || 'No asignado'}</p>
                </div>
                <div className="glass-deep rounded-lg p-4 border border-white/[0.08]">
                  <p className="text-sm text-muted mb-1">Estado</p>
                  <p className="text-white font-medium">{getStatusLabel(getModuleStatus(selectedModule))}</p>
                </div>
                <div className="glass-deep rounded-lg p-4 border border-white/[0.08]">
                  <p className="text-sm text-muted mb-1">Fecha</p>
                  <p className="text-white font-medium">
                    {selectedModule.start_date ? fmtDateLong(selectedModule.start_date) : 'Por definir'}
                  </p>
                </div>
                <div className="glass-deep rounded-lg p-4 border border-white/[0.08]">
                  <p className="text-sm text-muted mb-1">Horario</p>
                  <p className="text-white font-medium">
                    {fmtTimeRange(selectedModule.start_date, selectedModule.end_date)}
                  </p>
                </div>
              </div>

              {selectedModule.objective && (
                <div className="glass-deep rounded-lg p-4 border border-white/[0.08]">
                  <p className="text-sm text-muted mb-2">Objetivo</p>
                  <p className="text-white">{selectedModule.objective}</p>
                </div>
              )}

              {profile?.role === 'docente' && getModuleStatus(selectedModule) === 'active' && selectedModule.zoom_link && (
                <div className="space-y-3">
                  <a
                    href={selectedModule.zoom_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all duration-200 font-medium"
                  >
                    <ExternalLink className="w-5 h-5" />
                    Iniciar Reunión
                  </a>
                </div>
              )}

              {profile?.role === 'alumno' && getModuleStatus(selectedModule) === 'active' && (
                <div className="space-y-3">
                  <div className="flex gap-3">
                    {selectedModule.zoom_link && (
                      <a
                        href={selectedModule.zoom_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-all duration-200 font-medium"
                      >
                        <ExternalLink className="w-5 h-5" />
                        Unirse a Zoom
                      </a>
                    )}
                    {!attendance[selectedModule.id] && canMarkAttendance(selectedModule) && (
                      <button
                        onClick={() => markAttendance(selectedModule.id)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-cubus-lime hover:bg-cubus-lime-dim text-cubus-bg rounded-lg transition-all duration-200 font-medium"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Marcar Asistencia
                      </button>
                    )}
                  </div>
                  {!attendance[selectedModule.id] && !canMarkAttendance(selectedModule) && (
                    <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-sm text-yellow-400 text-center">
                        Solo puedes marcar asistencia el día del módulo
                      </p>
                    </div>
                  )}
                  {attendance[selectedModule.id] && (
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-sm text-green-400 text-center flex items-center justify-center gap-2">
                        <CheckCircle className="w-4 h-4" />
                        Asistencia registrada
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Visor de la presentación HTML interactiva del módulo.
                  Bloqueado para alumnos si la clase aún no ha llegado. */}
              {profile?.role === 'alumno' && getModuleStatus(selectedModule) === 'pending' ? (
                <div className="glass-deep rounded-lg border border-white/[0.08] opacity-60">
                  <div className="p-4 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 bg-white/[0.05]">
                      <Lock className="w-6 h-6 text-muted" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[2px] uppercase font-semibold text-muted mb-0.5">
                        Presentación interactiva
                        {selectedModule.class_number != null && (
                          <span className="text-muted/60 normal-case tracking-normal font-normal ml-2">
                            · Clase {String(selectedModule.class_number).padStart(2, '0')}
                          </span>
                        )}
                      </div>
                      <h4 className="text-sm font-medium text-white/60 truncate">{selectedModule.name}</h4>
                      <p className="text-xs text-muted mt-0.5">Disponible el día de la clase</p>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="text-xs px-2 py-1 rounded-full bg-gray-700 text-muted">Bloqueado</span>
                    </div>
                  </div>
                </div>
              ) : (
                <ModuleHTMLViewer
                  moduleId={selectedModule.id}
                  moduleName={selectedModule.name}
                  classNumber={selectedModule.class_number ?? null}
                  docente={selectedModule.docente_name ?? null}
                  numeroDiplomado={numeroDiplomado}
                  totalClases={modules.length}
                />
              )}

              {/* ── Tarea interactiva de la clase (solo alumno) ────────────── */}
              {profile?.role === 'alumno' && moduleTask && (
                <div className="glass-deep rounded-lg border border-white/[0.08] overflow-hidden">
                  <div className="p-4 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      taskSubmission ? 'bg-cubus-lime/10' : 'bg-white/[0.05]'
                    }`}>
                      <ClipboardCheck className={`w-6 h-6 ${taskSubmission ? 'text-cubus-lime' : 'text-white/50'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[2px] uppercase font-semibold text-muted mb-0.5">
                        Tarea de la clase
                      </div>
                      <h4 className="text-sm font-medium text-white truncate">{moduleTask.title}</h4>
                      {taskSubmission ? (
                        <p className="text-xs text-cubus-lime mt-0.5 font-mono">
                          Nota: {(Math.round((1 + ((taskSubmission.score_avg ?? taskSubmission.score) / 100) * 4) * 10) / 10).toFixed(1)} / 5.0
                        </p>
                      ) : !isTaskOpen(moduleTask) ? (
                        <p className="text-xs text-yellow-400 mt-0.5">Plazo cerrado</p>
                      ) : (
                        <p className="text-xs text-white/40 mt-0.5">Pendiente</p>
                      )}
                    </div>
                    <button
                      onClick={() => setIsTaskModalOpen(true)}
                      className={`flex-shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                        taskSubmission
                          ? 'bg-white/[0.05] hover:bg-white/[0.10] border border-white/10 text-white/70'
                          : isTaskOpen(moduleTask)
                          ? 'bg-cubus-lime hover:bg-cubus-lime/80 text-[#050505] font-bold'
                          : 'bg-white/[0.04] border border-white/10 text-white/30 cursor-not-allowed'
                      }`}
                      disabled={!isTaskOpen(moduleTask) && !taskSubmission}
                    >
                      <ClipboardCheck className="w-4 h-4" />
                      {taskSubmission ? 'Ver tarea' : 'Realizar tarea'}
                    </button>
                  </div>
                </div>
              )}

              {/* ── Gestión de tarea (docente / admin) ─────────────────────── */}
              {(profile?.role === 'docente' || profile?.role === 'admin') && (
                <div className="glass-deep rounded-lg border border-white/[0.08] overflow-hidden">
                  <div className="p-4 flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      moduleTask ? 'bg-cubus-lime/10' : 'bg-white/[0.05]'
                    }`}>
                      <ClipboardCheck className={`w-6 h-6 ${moduleTask ? 'text-cubus-lime' : 'text-white/40'}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] tracking-[2px] uppercase font-semibold text-muted mb-0.5">
                        Tarea de la clase
                      </div>
                      {moduleTask ? (
                        <h4 className="text-sm font-medium text-white truncate">{moduleTask.title}</h4>
                      ) : (
                        <p className="text-xs text-white/40">Sin tarea asignada a este módulo</p>
                      )}
                      {taskUploadMsg === 'ok' && (
                        <p className="text-xs text-cubus-lime mt-0.5">Tarea subida. Los alumnos ya la pueden ver.</p>
                      )}
                      {taskUploadMsg.startsWith('error:') && (
                        <p className="text-xs text-rose-400 mt-0.5">{taskUploadMsg.slice(6)}</p>
                      )}
                    </div>
                    <div className="flex-shrink-0 flex items-center gap-2">
                      {/* Borrar tarea */}
                      {moduleTask && !confirmDeleteTask && (
                        <button
                          onClick={() => setConfirmDeleteTask(true)}
                          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-white/[0.04] hover:bg-rose-500/10 border border-white/10 hover:border-rose-500/40 text-white/40 hover:text-rose-400 text-sm transition-all"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                      {moduleTask && confirmDeleteTask && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-xs text-rose-400">¿Borrar?</span>
                          <button onClick={handleDeleteTask} className="px-2.5 py-1.5 rounded-lg bg-rose-500/20 border border-rose-500/40 text-rose-400 text-xs font-bold hover:bg-rose-500/30 transition-all">Sí</button>
                          <button onClick={() => setConfirmDeleteTask(false)} className="px-2.5 py-1.5 rounded-lg bg-white/[0.05] border border-white/10 text-white/50 text-xs hover:bg-white/[0.10] transition-all">No</button>
                        </div>
                      )}
                      {/* Subir / reemplazar tarea */}
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          multiple
                          className="hidden"
                          ref={(el) => { if (el) el.setAttribute('webkitdirectory', ''); }}
                          onChange={handleTaskFolderUpload}
                          disabled={uploadingTask}
                        />
                        <span className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all select-none ${
                          uploadingTask
                            ? 'bg-white/[0.04] border-white/10 text-white/30 cursor-wait'
                            : 'bg-white/[0.06] hover:bg-white/[0.10] border-white/10 text-white/70 cursor-pointer'
                        }`}>
                          <Upload className="w-4 h-4" />
                          {uploadingTask ? 'Subiendo…' : moduleTask ? 'Reemplazar' : 'Subir tarea'}
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              )}

              <div className="border-t border-white/[0.06] pt-6">
                <div className="flex items-center gap-4 mb-6 border-b border-white/[0.08]">
                  <button
                    onClick={() => setActiveTab('files')}
                    className={`pb-3 px-4 font-medium transition-colors relative ${
                      activeTab === 'files'
                        ? 'text-cubus-lime'
                        : 'text-muted hover:text-white'
                    }`}
                  >
                    Archivos del Módulo
                    {activeTab === 'files' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cubus-lime"></div>
                    )}
                  </button>
                  <button
                    onClick={() => setActiveTab('submissions')}
                    className={`pb-3 px-4 font-medium transition-colors relative ${
                      activeTab === 'submissions'
                        ? 'text-cubus-lime'
                        : 'text-muted hover:text-white'
                    }`}
                  >
                    {profile?.role === 'alumno' ? 'Subir Archivo' : 'Archivos Subidos'}
                    {activeTab === 'submissions' && (
                      <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-cubus-lime"></div>
                    )}
                  </button>

                  {/* Botón subir recurso para docente/admin */}
                  {(profile?.role === 'docente' || profile?.role === 'admin') && (
                    <label className="ml-auto mb-2 cursor-pointer">
                      <input
                        type="file"
                        className="hidden"
                        onChange={handleModuleFileUpload}
                        disabled={uploadingModuleFile}
                        accept=".png,.jpeg,.jpg,.pdf,.rvt,.rfa,.rte,.dwg,.docx,.xlsx,.pptx,.zip,.rar"
                      />
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border transition-all text-sm font-medium ${
                        uploadingModuleFile
                          ? 'border-white/10 text-muted cursor-not-allowed'
                          : 'border-cubus-lime/40 text-cubus-lime hover:bg-cubus-lime/10 cursor-pointer'
                      }`}>
                        <Upload className="w-4 h-4" />
                        {uploadingModuleFile ? 'Subiendo...' : 'Subir recurso'}
                      </div>
                    </label>
                  )}
                </div>

                {activeTab === 'files' ? (
                  (selectedModule.materials.length > 0 || selectedModule.module_files.length > 0) ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {selectedModule.module_files.map((file) => {
                        const Icon = getFileIcon(file.file_type);
                        const fileSizeMB = (file.file_size / (1024 * 1024)).toFixed(2);
                        const moduleStatus = getModuleStatus(selectedModule);
                        const isAccessible = profile?.role !== 'alumno' || moduleStatus === 'active' || moduleStatus === 'completed';
                        const isLocked = profile?.role === 'alumno' && moduleStatus === 'pending';

                        const content = (
                          <>
                            <div className={`w-12 h-12 bg-cubus-lime/10 rounded-lg flex items-center justify-center flex-shrink-0 ${isLocked ? 'opacity-50' : ''}`}>
                              <Icon className="w-6 h-6 text-cubus-lime" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className={`font-medium ${isLocked ? 'text-subtle' : 'text-white group-hover:text-cubus-lime'} transition-colors truncate`}>
                                  {file.file_name}
                                </h4>
                                {isLocked && (
                                  <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-md border border-red-500/30 whitespace-nowrap">
                                    🔒 Bloqueado
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="inline-block px-2 py-1 bg-gray-700 rounded text-xs text-white/70">
                                  {file.file_type.toUpperCase()}
                                </span>
                                <span className="text-xs text-muted">
                                  {fileSizeMB} MB
                                </span>
                              </div>
                            </div>
                          </>
                        );

                        if (isAccessible) {
                          return (
                            <button
                              key={file.id}
                              onClick={() => handleDownload(file.file_url, file.file_name)}
                              className="flex items-start gap-4 p-4 glass-deep border border-white/[0.08] hover:border-cubus-lime/40 rounded-lg transition-all duration-200 group w-full text-left"
                            >
                              {content}
                            </button>
                          );
                        } else {
                          return (
                            <div
                              key={file.id}
                              className="flex items-start gap-4 p-4 glass-deep border border-white/[0.08] rounded-lg opacity-60 cursor-not-allowed"
                              title="Este archivo estará disponible cuando el módulo esté activo"
                            >
                              {content}
                            </div>
                          );
                        }
                      })}
                      {selectedModule.materials.map((material) => {
                        const Icon = getFileIcon(material.file_type);
                        const moduleStatus = getModuleStatus(selectedModule);
                        const isAccessible = profile?.role !== 'alumno' || moduleStatus === 'active' || moduleStatus === 'completed';
                        const isLocked = profile?.role === 'alumno' && moduleStatus === 'pending';

                        const content = (
                          <>
                            {material.thumbnail_url ? (
                              <img
                                src={material.thumbnail_url}
                                alt={material.title}
                                className={`w-20 h-20 object-cover rounded-lg ${isLocked ? 'opacity-50 grayscale' : ''}`}
                              />
                            ) : (
                              <div className={`w-20 h-20 bg-gray-700 rounded-lg flex items-center justify-center ${isLocked ? 'opacity-50' : ''}`}>
                                <Icon className="w-8 h-8 text-muted" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className={`font-medium ${isLocked ? 'text-subtle' : 'text-white group-hover:text-cubus-lime'} transition-colors`}>
                                  {material.title}
                                </h4>
                                {isLocked && (
                                  <span className="px-2 py-1 bg-red-500/10 text-red-400 text-xs rounded-md border border-red-500/30">
                                    🔒 Bloqueado
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-muted mb-2">{material.description}</p>
                              <span className="inline-block px-2 py-1 bg-gray-700 rounded text-xs text-white/70">
                                {material.file_type.toUpperCase()}
                              </span>
                            </div>
                          </>
                        );

                        if (isAccessible) {
                          // Videos: abrir en modal (no descargar).
                          if (material.file_type === 'video') {
                            return (
                              <button
                                key={material.id}
                                onClick={() => {
                                  setSelectedVideo({
                                    url: material.file_url,
                                    title: material.title,
                                    embedCode: material.embed_code
                                  });
                                  setIsVideoModalOpen(true);
                                }}
                                className="flex items-start gap-4 p-4 glass-deep border border-white/[0.08] hover:border-cubus-lime/40 rounded-lg transition-all duration-200 group w-full text-left"
                              >
                                {content}
                              </button>
                            );
                          }

                          // Guías HTML: abrir en modal full-screen (no descargar —
                          // la URL es cross-origin y fetch fallaría con CORS).
                          if (material.file_type === 'html_guide') {
                            return (
                              <button
                                key={material.id}
                                onClick={() => {
                                  setSelectedHTML({ url: material.file_url, title: material.title });
                                  setIsHTMLViewerOpen(true);
                                }}
                                className="flex items-start gap-4 p-4 glass-deep border border-white/[0.08] hover:border-cubus-lime/40 rounded-lg transition-all duration-200 group w-full text-left"
                              >
                                {content}
                              </button>
                            );
                          }

                          // PDFs: abrir en visor inline (no descargar).
                          if (material.file_type === 'pdf') {
                            return (
                              <button
                                key={material.id}
                                onClick={() => {
                                  setSelectedPDF({ url: material.file_url, title: material.title });
                                  setIsPDFViewerOpen(true);
                                }}
                                className="flex items-start gap-4 p-4 glass-deep border border-white/[0.08] hover:border-cubus-lime/40 rounded-lg transition-all duration-200 group w-full text-left"
                              >
                                {content}
                              </button>
                            );
                          }

                          return (
                            <button
                              key={material.id}
                              onClick={() => handleDownload(material.file_url, material.title)}
                              className="flex items-start gap-4 p-4 glass-deep border border-white/[0.08] hover:border-cubus-lime/40 rounded-lg transition-all duration-200 group w-full text-left"
                            >
                              {content}
                            </button>
                          );
                        } else {
                          return (
                            <div
                              key={material.id}
                              className="flex items-start gap-4 p-4 glass-deep border border-white/[0.08] rounded-lg opacity-60 cursor-not-allowed"
                              title="Este material estará disponible cuando el módulo esté activo"
                            >
                              {content}
                            </div>
                          );
                        }
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-faint mx-auto mb-3" />
                      <p className="text-muted">No hay archivos en este módulo</p>
                    </div>
                  )
                ) : profile?.role === 'alumno' ? (
                  <div>
                    {studentSubmission ? (
                      <div className="space-y-4">
                        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                          <p className="text-green-400 font-medium mb-2 flex items-center gap-2">
                            <FileCheck className="w-5 h-5" />
                            Archivo subido exitosamente
                          </p>
                        </div>
                        <div className="flex items-start gap-4 p-4 glass-deep border border-white/[0.08] rounded-lg">
                          <div className="w-12 h-12 bg-cubus-lime/10 rounded-lg flex items-center justify-center flex-shrink-0">
                            <FileDown className="w-6 h-6 text-cubus-lime" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-white mb-1 truncate">{studentSubmission.file_name}</h4>
                            <p className="text-xs text-muted">
                              Subido el {new Date(studentSubmission.submitted_at).toLocaleString('es-ES')}
                            </p>
                            <div className="mt-2 flex items-center gap-2">
                              <span className="text-xs text-muted">Nota:</span>
                              {(() => {
                                const gradeValue = typeof studentSubmission.grade === 'string' ? parseFloat(studentSubmission.grade) : studentSubmission.grade;
                                return gradeValue === 0 || gradeValue === null || isNaN(gradeValue) ? (
                                  <span className="text-sm text-yellow-400 font-medium">Pendiente</span>
                                ) : (
                                  <span className="text-sm text-cubus-lime font-bold">{gradeValue.toFixed(1)}</span>
                                );
                              })()}
                            </div>
                          </div>
                          <button
                            onClick={() => handleDownload(studentSubmission.file_url, studentSubmission.file_name)}
                            className="px-3 py-2 bg-cubus-lime/10 text-cubus-lime rounded-lg hover:bg-cubus-lime/20 transition-all text-sm"
                          >
                            Descargar
                          </button>
                        </div>
                        <label className="block">
                          <input
                            type="file"
                            onChange={handleFileUpload}
                            disabled={uploadingFile}
                            className="hidden"
                            accept=".png,.jpeg,.jpg,.pdf,.rvt,.rfa,.rte,.dwg,.docx,.xlsx,.pptx,.zip,.rar"
                          />
                          <div className="flex items-center justify-center gap-2 px-4 py-3 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all cursor-pointer border border-blue-500/30">
                            <Upload className="w-5 h-5" />
                            <span>{uploadingFile ? 'Subiendo...' : 'Reemplazar archivo'}</span>
                          </div>
                        </label>
                      </div>
                    ) : (
                      <div className="text-center py-12">
                        <Upload className="w-16 h-16 text-faint mx-auto mb-4" />
                        <p className="text-muted mb-6">No has subido ningún archivo para este módulo</p>
                        <label className="inline-block">
                          <input
                            type="file"
                            onChange={handleFileUpload}
                            disabled={uploadingFile}
                            className="hidden"
                            accept=".png,.jpeg,.jpg,.pdf,.rvt,.rfa,.rte,.dwg,.docx,.xlsx,.pptx,.zip,.rar"
                          />
                          <div className="flex items-center justify-center gap-2 px-6 py-3 bg-cubus-lime text-cubus-bg rounded-lg hover:bg-cubus-lime-dim transition-all cursor-pointer font-semibold">
                            <Upload className="w-5 h-5" />
                            <span>{uploadingFile ? 'Subiendo...' : 'Subir archivo'}</span>
                          </div>
                        </label>
                        <p className="text-xs text-subtle mt-4">
                          Formatos permitidos: PNG, JPEG, PDF, RVT, RFA, RTE, DWG, DOCX, XLSX, PPTX, ZIP, RAR
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    {submissions.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 text-faint mx-auto mb-3" />
                        <p className="text-muted">No hay archivos subidos por los alumnos</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-white/[0.08]">
                              <th className="text-left p-3 text-sm font-semibold text-white/70">Alumno</th>
                              <th className="text-left p-3 text-sm font-semibold text-white/70">Archivo</th>
                              <th className="text-left p-3 text-sm font-semibold text-white/70">Fecha de Entrega</th>
                              <th className="text-left p-3 text-sm font-semibold text-white/70">Nota</th>
                            </tr>
                          </thead>
                          <tbody>
                            {submissions.map((submission: any) => (
                              <tr key={submission.id} className="border-b border-white/[0.06] hover:bg-white/[0.04]">
                                <td className="p-3 text-white">{submission.profiles?.full_name || 'N/A'}</td>
                                <td className="p-3">
                                  <button
                                    onClick={() => handleDownload(submission.file_url, submission.file_name)}
                                    className="text-cubus-lime hover:underline flex items-center gap-2"
                                  >
                                    <FileDown className="w-4 h-4" />
                                    {submission.file_name}
                                  </button>
                                </td>
                                <td className="p-3 text-muted text-sm">
                                  {new Date(submission.submitted_at).toLocaleString('es-ES')}
                                </td>
                                <td className="p-3">
                                  {gradingSubmissionId === submission.id ? (
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="number"
                                        min="1.0"
                                        max="7.0"
                                        step="0.1"
                                        value={gradeInputs[submission.id] || ''}
                                        onChange={(e) => setGradeInputs(prev => ({ ...prev, [submission.id]: e.target.value }))}
                                        placeholder="1.0 - 7.0"
                                        className="w-20 px-2 py-1 glass-deep border border-white/[0.1] rounded text-white text-sm focus:outline-none focus:border-cubus-lime"
                                        autoFocus
                                      />
                                      <button
                                        onClick={() => handleGradeSubmit(submission.id)}
                                        className="px-3 py-1 bg-green-500 hover:bg-green-600 text-white rounded text-sm transition-colors"
                                      >
                                        ✓
                                      </button>
                                      <button
                                        onClick={() => {
                                          setGradingSubmissionId(null);
                                          setGradeInputs(prev => {
                                            const updated = { ...prev };
                                            delete updated[submission.id];
                                            return updated;
                                          });
                                        }}
                                        className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm transition-colors"
                                      >
                                        ✕
                                      </button>
                                    </div>
                                  ) : (
                                    <button
                                      onClick={() => {
                                        setGradingSubmissionId(submission.id);
                                        const currentGrade = typeof submission.grade === 'string' ? parseFloat(submission.grade) : submission.grade;
                                        setGradeInputs(prev => ({ ...prev, [submission.id]: currentGrade > 0 ? currentGrade.toString() : '' }));
                                      }}
                                      className="text-white hover:text-cubus-lime transition-colors"
                                    >
                                      {(typeof submission.grade === 'string' ? parseFloat(submission.grade) : submission.grade) === 0 || submission.grade === null ? (
                                        <span className="text-yellow-400">Sin nota</span>
                                      ) : (
                                        <span className="font-bold">{(typeof submission.grade === 'string' ? parseFloat(submission.grade) : submission.grade).toFixed(1)}</span>
                                      )}
                                    </button>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedVideo && (
        <VideoPlayerModal
          isOpen={isVideoModalOpen}
          onClose={() => {
            setIsVideoModalOpen(false);
            setSelectedVideo(null);
          }}
          videoUrl={selectedVideo.url}
          videoTitle={selectedVideo.title}
          embedCode={selectedVideo.embedCode}
        />
      )}

      <HTMLViewerModal
        open={isHTMLViewerOpen}
        url={selectedHTML?.url ?? null}
        title={selectedHTML?.title ?? ''}
        onClose={() => { setIsHTMLViewerOpen(false); setSelectedHTML(null); }}
      />

      <PDFViewerModal
        open={isPDFViewerOpen}
        url={selectedPDF?.url ?? null}
        title={selectedPDF?.title ?? ''}
        onClose={() => { setIsPDFViewerOpen(false); setSelectedPDF(null); }}
      />

      {isTaskModalOpen && moduleTask && profile?.id && (
        <TaskModal
          task={moduleTask}
          studentId={profile.id}
          existingSubmission={taskSubmission}
          onClose={() => setIsTaskModalOpen(false)}
          onSubmitted={(sub) => {
            setTaskSubmission(sub);
            // No cierra el modal — deja que el alumno vea el resultado y haga retry
          }}
        />
      )}
    </div>
  );
}
